import * as React from 'react';
import { ReactNode, useMemo, useRef } from 'react';
import { DataTreeDragPreview } from 'shared/data_tree/DataTreeDragPreview';
import { DragDropContainer } from 'shared/data_tree/styled';
import {
    TreeMovableConfig,
    TreeNode,
    TreeNodeId,
    TreeNodePosition,
    UseTreeDragDropNodeInfo,
    UseTreeDragDropTargetNodeInfo,
} from 'shared/data_tree/types';
import { useTreeDragDrop } from 'shared/data_tree/useTreeDragDrop';
import { useDragSourceHandlers } from 'shared/drag_drop/DragSource';
import { shallow } from 'zustand/shallow';

/**
 * The percentage of the total height of a node that is considered its edge.
 * This zone is used when dragging a node over another node to determine
 * if the node is "before", "after", or a "child" of the drop target node.
 * Example: If a node were 100px in height, the top and bottom edges would
 * each be 25px in height.
 *
 *  Drag Target Node (100px)
 * +---------------+
 * | before (25px) |
 * +---------------+
 * |    child      |
 * +---------------+
 * | after  (25px) |
 * +---------------+
 */
const HoverEdgeZone = 0.25;

type PreviewPosition = Readonly<{ x: number; y: number }>;

type ValidCheck = PreviewPosition &
    Readonly<{
        isValid: boolean;
    }>;

const getTargetMoveType = (
    event: React.DragEvent<HTMLElement>,
): TreeNodePosition | undefined => {
    const { pageY, target } = event;
    if (target instanceof HTMLElement) {
        const { top, height } = target.getBoundingClientRect();
        const offset = pageY - top;
        const edgeZone = height * HoverEdgeZone;

        if (offset < edgeZone) {
            return 'before';
        }
        if (offset > height - edgeZone) {
            return 'after';
        }
        return 'child';
    }
    return undefined;
};

type GetBorderColorsOptions = Readonly<{
    treeId: string;
    dragNode: UseTreeDragDropNodeInfo | undefined;
    targetNode: UseTreeDragDropTargetNodeInfo | undefined;
    canMoveTo: () => boolean | undefined;
    nodeId: TreeNodeId;
}>;

const getDragIndicatorPosition = ({
    treeId,
    dragNode,
    targetNode,
    nodeId,
    canMoveTo,
}: GetBorderColorsOptions): 'before' | 'after' | 'around' | null => {
    if (
        !dragNode ||
        !targetNode ||
        dragNode.nodeId === nodeId ||
        targetNode.treeId !== treeId
    ) {
        return null;
    }

    if (!canMoveTo()) {
        return null;
    }

    if (nodeId === targetNode.nodeId && targetNode.moveType === 'before') {
        return 'before';
    }

    if (nodeId === targetNode.nodeId && targetNode.moveType === 'after') {
        return 'after';
    }

    if (
        (nodeId === targetNode.nodeId && targetNode.moveType === 'child') ||
        (nodeId === targetNode.parentNodeId && targetNode.moveType !== 'child')
    ) {
        return 'around';
    }

    return null;
};

export type TreeDragDropProps<TData> = Readonly<{
    treeId: string;
    node: TreeNode<TData>;
    parentNodeId?: TreeNodeId;
    movable?: TreeMovableConfig<TData>;
    children?: ReactNode;
    isMovable: boolean;
}>;

export function TreeDragDrop<TData>({
    treeId,
    node,
    parentNodeId,
    movable,
    isMovable,
    children,
}: TreeDragDropProps<TData>) {
    const prevValidCheckRef = useRef<ValidCheck>();

    const {
        dragNode,
        targetNode,
        startMove,
        updateTarget,
        clearTarget,
        endMove,
        updatePreviewPosition,
    } = useTreeDragDrop(
        (state) => ({
            dragNode: state.dragNode,
            targetNode: state.targetNode,
            startMove: state.startMove,
            updateTarget: state.updateTarget,
            clearTarget: state.clearTarget,
            endMove: state.endMove,
            updatePreviewPosition: state.updatePreviewPosition,
        }),
        shallow,
    );

    const isNodeBeingMoved =
        movable && dragNode && dragNode.treeId === treeId && dragNode.nodeId === node.id;

    const dragSourceHandlers = useDragSourceHandlers({
        onStart: (x, y) => startMove({ treeId, nodeId: node.id }, x, y),
        onMove: updatePreviewPosition,
        onEnd: endMove,
        isDragging: !!isNodeBeingMoved,
        disabled: !isMovable,
    });

    const onDragOver = (event: React.DragEvent<HTMLElement>) => {
        const { dataTransfer } = event.nativeEvent;

        const complete = (isValid: boolean) => {
            prevValidCheckRef.current = { x: event.pageX, y: event.pageY, isValid };
            if (dataTransfer) {
                dataTransfer.dropEffect = isValid ? 'move' : 'none';
            }
            event.preventDefault();
            event.stopPropagation();
        };

        if (!movable || !dragNode || dragNode.nodeId === node.id) {
            complete(false);
        } else if (
            event.pageX === prevValidCheckRef.current?.x &&
            event.pageY === prevValidCheckRef.current.y
        ) {
            complete(prevValidCheckRef.current.isValid);
        } else {
            const moveType = getTargetMoveType(event);

            const moveInfo = moveType
                ? {
                      treeId: dragNode.treeId,
                      nodeId: dragNode.nodeId,
                      moveType,
                      targetTreeId: treeId,
                      targetNodeId: node.id,
                  }
                : undefined;

            const isValid = moveInfo !== undefined && movable.canMoveTo(moveInfo);

            if (moveInfo) {
                updateTarget({
                    treeId,
                    nodeId: node.id,
                    moveType: moveInfo.moveType,
                    parentNodeId,
                    isValid,
                });
            } else {
                clearTarget();
            }

            complete(isValid);
        }
    };

    const onDrop = (event: React.DragEvent<HTMLElement>) => {
        if (movable && dragNode) {
            endMove();
            if (dragNode.nodeId !== node.id) {
                const moveType = getTargetMoveType(event);

                const moveInfo = moveType
                    ? {
                          treeId: dragNode.treeId,
                          nodeId: dragNode.nodeId,
                          moveType,
                          targetTreeId: treeId,
                          targetNodeId: node.id,
                      }
                    : undefined;

                const isValid = moveInfo !== undefined && movable.canMoveTo(moveInfo);

                if (moveInfo && isValid) {
                    movable.onMove(moveInfo);
                }
            }
        }
    };

    const dragIndicatorPosition = useMemo(
        () =>
            getDragIndicatorPosition({
                treeId,
                nodeId: node.id,
                dragNode,
                targetNode,
                canMoveTo: () =>
                    targetNode &&
                    dragNode &&
                    movable?.canMoveTo({
                        moveType: targetNode.moveType,
                        nodeId: dragNode.nodeId,
                        targetNodeId: targetNode.nodeId,
                        treeId: dragNode.treeId,
                        targetTreeId: targetNode.treeId,
                    }),
            }),
        [dragNode, movable, node.id, targetNode, treeId],
    );

    const { style: dragStyle, ...dragProps } = dragSourceHandlers;

    return (
        <DragDropContainer
            {...dragProps}
            onDragEnter={onDragOver}
            onDragExit={clearTarget}
            onDragOver={onDragOver}
            onDrop={onDrop}
            $dragIndicatorPosition={dragIndicatorPosition}
        >
            <div style={dragStyle}>{children}</div>
            {movable && isNodeBeingMoved && (
                <DataTreeDragPreview<TData> node={node} movable={movable} />
            )}
        </DragDropContainer>
    );
}
